import {
  getAdaptedSummaryTable,
  transformIndexToPricesStructure,
} from '~/utils/adapters'
import { MARKET_TYPE, MARKET_TYPES } from '~/src/constants/markets'
import { reformat } from '~/utils/date'
import { useReports } from '~/composables/useReports'
import type { TMarketSlug } from '~/src/types/markets'
import type { TReportCard } from '~/src/types/reports'
import { getUpcomingEvents } from '~/src/utils/events'

export const usePortal = () => {
  const { $graphqlClient, $restClient } = useNuxtApp()
  const { getReportAndModelData } = useReports()

  const getCriticalInsights = async (slug: TMarketSlug) => {
    const reports = await getReportAndModelData(
      slug,
      [MARKET_TYPE.CRITICAL_INSIGHTS],
      true,
      1,
    )
    return [...reports].map((report) => ({
      ...report,
      backgroundImage: report.images.desktop,
      publishedDate: reformat(report.date, 'dd MMM yyyy'),
    }))
  }

  const getArticles = async (slug: TMarketSlug, categories?: string[]) => {
    try {
      const [portalArticles, criticalInsights] = await Promise.all([
        $graphqlClient.getPortalArticles({
          market: slug,
          categories: categories || null,
        }),
        getCriticalInsights(slug),
      ])
      return [...portalArticles.articles]
        .toSpliced(2, 0, ...criticalInsights)
        .slice(0, 5)
    } catch (error) {
      logError(error)
      return []
    }
  }

  const getPricesPortal = async (market: string) => {
    try {
      const response = await $restClient.getPricesPortalByMineral(market)

      if (!response || (!response.prices && !response.indexes)) {
        return null
      }

      if (response.prices) {
        return {
          isPrices: true,
          pricesPortal: response.prices.chart,
          pricesSummaryPortal: getAdaptedSummaryTable({
            summaries: response.prices.table,
          }),
        }
      }
      const pricesPortal = response.indexes.detail.map((item) => ({
        category: {
          name: item.product,
        },
        currency: {
          symbol: '',
        },
        series: item.data.map((seriesData) => ({
          valueMid: seriesData.value,
          date: seriesData.date,
        })),
      }))

      const pricesSummaryPortal = [transformIndexToPricesStructure(response)]
      return {
        isPrices: false,
        pricesPortal,
        pricesSummaryPortal,
      }
    } catch (error) {
      logError(error)
      return null
    }
  }

  const getReports = (slug: TMarketSlug): Promise<TReportCard[]> => {
    const marketTypes = MARKET_TYPES.filter(
      (type) => type !== MARKET_TYPE.CRITICAL_INSIGHTS,
    )
    try {
      return getReportAndModelData(slug, marketTypes)
    } catch (error) {
      logError(error)
      return Promise.resolve([])
    }
  }

  const getVideos = async ({
    categories,
    slug,
  }: {
    slug: string
    categories?: string[]
  }) => {
    const categoriesRequest = categories?.length > 0 ? categories : [slug]
    const allVideos = await $graphqlClient.getVideos(categoriesRequest)

    if (allVideos.length === 0) {
      return []
    }

    const featuredVideo = allVideos.shift()
    allVideos.push(featuredVideo)

    if (allVideos.length >= 2 && allVideos.length <= 3) {
      allVideos.push(...allVideos)
    }

    return allVideos
  }

  const getEvents = async () => {
    const events = await $graphqlClient.getEvents()
    return getUpcomingEvents(events).slice(0, 10)
  }

  return {
    getArticles,
    getPricesPortal,
    getReports,
    getVideos,
    getEvents,
  }
}
